import { useState, useEffect, useCallback, useMemo, createContext, useContext, type ReactNode } from 'react'
import { emitCalendarChanged } from '@/lib/calendarChangedSignal'
import { shareInFlight } from '@/lib/sharedRequest'
import { getCurrentAccount } from '@/lib/currentAccount'
import { recentEventsFor, rememberEvents, RECENT_EVENTS_MS } from './recentCalendarEvents'
import { setCalendarConnected } from '@/lib/calendarConnection'
import { homeAccount } from '@/lib/calendar/homeAccount'
import { supabase, getAuthUser } from '@/lib/supabase'
import { afterPageData } from '@/lib/loadPriority'
import { logger } from '@/lib/logger'
import { getRecurringBaseId } from './useHiddenCalendarEvents'
import { filterOutEvent } from './calendarEventCache'
import { authUrlFunctionFor, functionErrorBody, isCalendarProvider, type CalendarProvider } from '@/lib/calendarProviders'

export type { CalendarProvider }

export interface CreateEventParams {
  title: string
  description?: string
  startTime: Date
  endTime: Date
  location?: string
  allDay?: boolean
  /** Optional timezone (e.g., 'America/New_York'). Defaults to browser timezone. */
  timeZone?: string
  /** Optional idempotency key to prevent duplicate events on retry */
  requestId?: string
  /** Optional target calendar ID. Defaults to 'primary' server-side. */
  calendarId?: string
  /** Optional recurrence lines ("RRULE:FREQ=WEEKLY;BYDAY=TU,TH") — makes a series. */
  recurrence?: string[]
}

export interface MoveEventParams {
  eventId: string
  sourceCalendarId: string
  destinationCalendarId: string
}

/** Error thrown when calendar needs reconnection due to expired/revoked permissions */
export class CalendarReconnectError extends Error {
  constructor(message: string = 'Calendar connection expired. Please reconnect.') {
    super(message)
    this.name = 'CalendarReconnectError'
  }
}

export interface CreateEventResult {
  id: string
  htmlLink?: string
}

export interface CalendarEvent {
  // Support both snake_case (from edge function) and camelCase (possibly cached/transformed)
  id: string
  google_event_id?: string
  title: string
  description?: string | null
  // Snake case from API
  start_time?: string
  end_time?: string
  all_day?: boolean
  calendar_id?: string
  calendar_name?: string | null
  calendar_color?: string | null // Google Calendar color (hex)
  recurring_event_id?: string | null // Set when this event is an instance of a recurring series
  // Camel case (observed in runtime)
  startTime?: string
  endTime?: string
  allDay?: boolean
  calendarId?: string
  calendarName?: string | null
  calendarColor?: string | null
  recurringEventId?: string | null
  location?: string | null
  // Video meeting join URL. Set from Google's hangoutLink (Meet) or the first
  // 'video' entryPoint in conferenceData (Zoom / Webex / Teams via add-ons).
  meeting_url?: string | null
  meetingUrl?: string | null
  // Attendees (from Google Calendar API)
  attendees?: { email: string; displayName?: string; responseStatus?: string; self?: boolean }[]
  // Set on synthetic meal events (from the meal plan) so the wall can open the
  // linked recipe's stored ingredients/instructions, not just a source URL.
  recipeId?: string | null
  /** Synthetic meal events: the plan entry's notes ("Roast double sweet
   *  potatoes — half for Wednesday"), for the wall's dinner cue. */
  mealNotes?: string | null
  /** Which account the event came from. Absent on cached/synthetic events = Google. */
  provider?: CalendarProvider
  /** The connected account (calendar_connections.id) it was read through. */
  connection_id?: string
}

export interface DeleteEventParams {
  eventId: string
  calendarId?: string
  /** When true, deletes the entire recurring series, not just this instance. */
  deleteSeries?: boolean
}

export interface UpdateEventParams {
  eventId: string
  /** New event title (Google `summary`). */
  title?: string
  location?: string | null
  startTime?: Date
  endTime?: Date
  /** IANA timezone (e.g., 'America/New_York'). Defaults to browser tz. */
  timeZone?: string
  calendarId?: string // Optional calendar ID (defaults to 'primary')
}

export interface GoogleCalendarInfo {
  id: string
  summary: string
  email: string
  accessRole: 'owner' | 'writer' | 'reader'
  primary: boolean
  backgroundColor?: string
  /** Absent = Google (older mocks / callers). */
  provider?: CalendarProvider
  /** The connected account this calendar belongs to. */
  connectionId?: string
  accountEmail?: string | null
}

/** One connected calendar account. A person may have several per provider. */
export interface CalendarAccount {
  id: string
  provider: CalendarProvider
  /** Null until the account has been read once after multi-account shipped. */
  accountEmail: string | null
  /** Google: where new events go and mail is read, unless something names another. */
  isPrimary: boolean
  /** The account granted Gmail access. */
  grantsMail: boolean
}

const GMAIL_READ_SCOPE = 'https://www.googleapis.com/auth/gmail.readonly'

interface ConnectionRow {
  id: string
  provider: string
  calendar_id: string | null
  account_email?: string | null
  is_primary?: boolean | null
  granted_scopes?: string | null
}

/** Rows as accounts, primary first, then by email. */
export function toCalendarAccounts(rows: ConnectionRow[]): CalendarAccount[] {
  return rows
    .filter((r) => isCalendarProvider(r.provider))
    .map((r) => ({
      id: r.id,
      provider: r.provider as CalendarProvider,
      accountEmail: r.account_email ?? null,
      // Before the multi-account migration every row was its provider's only one.
      isPrimary: r.is_primary ?? true,
      grantsMail: typeof r.granted_scopes === 'string' && r.granted_scopes.split(/\s+/).includes(GMAIL_READ_SCOPE),
    }))
    .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary) || (a.accountEmail ?? '').localeCompare(b.accountEmail ?? ''))
}

interface GoogleCalendarContextValue {
  /** True when ANY provider is connected. */
  isConnected: boolean
  /** Which providers have a stored connection. */
  connectedProviders: CalendarProvider[]
  /** Every connected account, primary first. */
  accounts: CalendarAccount[]
  /** Accounts whose grant was revoked while another still works. */
  reconnectAccountIds: string[]
  /** True when every connection is broken and the user must reconnect. */
  needsReconnect: boolean
  /** Providers whose grant was revoked while another still works. */
  reconnectProviders: CalendarProvider[]
  /** Google withheld the calendar list, so only the primary calendar shows. */
  calendarListMissing: boolean
  isLoading: boolean
  isFetching: boolean
  events: CalendarEvent[]
  error: string | null
  connect: (provider?: CalendarProvider, options?: { mail?: boolean }) => Promise<void>
  /** Disconnect one account, or every account of a provider. */
  disconnect: (target?: CalendarProvider | { connectionId: string }) => Promise<void>
  /** Make this Google account the one new events and mail go through. */
  makePrimary: (connectionId: string) => Promise<void>
  fetchEvents: (startDate: Date, endDate: Date, domainOverride?: string) => Promise<CalendarEvent[]>
  fetchTodayEvents: () => Promise<CalendarEvent[]>
  fetchWeekEvents: () => Promise<CalendarEvent[]>
  fetchCalendarList: () => Promise<GoogleCalendarInfo[]>
  createEvent: (params: CreateEventParams) => Promise<CreateEventResult>
  updateEvent: (params: UpdateEventParams) => Promise<void>
  moveEvent: (params: MoveEventParams) => Promise<void>
  deleteEvent: (params: DeleteEventParams) => Promise<void>
  /** Optimistically remove an event from the local cache (caller orchestrates undo). */
  removeEventLocal: (eventId: string) => void
  /** Restore a previously-removed event into the local cache (used by undo). */
  restoreEventLocal: (event: CalendarEvent) => void
  /** Default calendar for Symphony-created events (null = the home account's own calendar). */
  defaultCalendarId: string | null
  /** Persist a new default write calendar (null = the home account's own calendar). */
  setDefaultCalendarId: (calendarId: string | null) => Promise<void>
}

const GoogleCalendarContext = createContext<GoogleCalendarContextValue | null>(null)

export function GoogleCalendarProvider({ children }: { children: ReactNode }) {

  const [isConnected, setIsConnected] = useState(false)
  // Published for readers that are not components — the day tiles' planning
  // calendar, which must tell "no calendar" from "could not read it".
  useEffect(() => { setCalendarConnected(isConnected) }, [isConnected])
  const [accounts, setAccounts] = useState<CalendarAccount[]>([])
  const connectedProviders = useMemo(() => [...new Set(accounts.map((a) => a.provider))], [accounts])
  const [reconnectAccountIds, setReconnectAccountIds] = useState<string[]>([])
  const [needsReconnect, setNeedsReconnect] = useState(false)
  const [reconnectProviders, setReconnectProviders] = useState<CalendarProvider[]>([])
  const [calendarListMissing, setCalendarListMissing] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isFetching, setIsFetching] = useState(false)
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [error, setError] = useState<string | null>(null)
  // Where Symphony-created events land when no calendar is specified.
  // null = the home account's own calendar. Stored on that account's calendar_connections.calendar_id.
  const [defaultCalendarId, setDefaultCalendarIdState] = useState<string | null>(null)

  // Check connection status and validate token.
  //
  // This used to run exactly once, on mount. On the kitchen wall that is a
  // race the Pi loses: Chromium opens the app before Wi-Fi is up, the session
  // restore and the calendar_connections read both fail, isConnected stays
  // false, and every later poll returns [] without asking again — a wall with
  // tasks and routines and no calendar until someone reloads it (2026-09-03).
  // So a check that fails for any reason other than a real "reconnect needed"
  // tries again, and a sign-in / token refresh triggers a fresh check.
  useEffect(() => {
    let cancelled = false
    let retryTimer: ReturnType<typeof setTimeout> | null = null
    let attempts = 0
    // One check at a time, and none again soon after a good one (2026-10-18).
    // supabase-js announces SIGNED_IN on every session restore — on page load,
    // right beside the mount check, and on every return to the tab — so each
    // load validated twice and every tab switch called the calendar again,
    // each one a full trip to Google.
    let running: Promise<void> | null = null
    let checkingFor: string | null = null
    let validatedFor: { userId: string; at: number } | null = null

    const runCheck = (opts: { sessionUserId?: string } = {}): Promise<void> => {
      if (running) {
        // A session for someone else than the check in flight: look again after it.
        if (opts.sessionUserId && checkingFor !== opts.sessionUserId) return running.then(() => runCheck(opts))
        return running
      }
      // Fresh for this session's user; a different user always rechecks.
      if (
        opts.sessionUserId && validatedFor && validatedFor.userId === opts.sessionUserId &&
        Date.now() - validatedFor.at < CONNECTION_RECHECK_AFTER_MS
      ) return Promise.resolve()
      running = checkAndValidateConnection().finally(() => { running = null })
      return running
    }

    const scheduleRetry = () => {
      if (cancelled || attempts >= CONNECTION_CHECK_MAX_ATTEMPTS) return
      attempts += 1
      retryTimer = setTimeout(() => { void runCheck() }, CONNECTION_CHECK_RETRY_MS)
    }

    async function checkAndValidateConnection() {
      if (cancelled) return
      try {
        const { data: { user } } = await getAuthUser()
        checkingFor = user?.id ?? null
        if (!user) {
          setIsConnected(false)
          setNeedsReconnect(false)
          setIsLoading(false)
          scheduleRetry()
          return
        }

        // One row per connected provider (Google, Outlook).
        //
        // A plain list read, never `single()`. `single()` asks PostgREST for
        // exactly one row and returns 406 when it gets none — so "this user
        // has no calendar connection" arrived as an ERROR, which is the one
        // thing the retry below is for. The result was a signed-out or
        // RLS-invisible session retrying 20 times over ten minutes and filling
        // the console with 406s, when the honest answer was "not connected"
        // on the first try (2026-09-04, prod). An empty list is an answer.
        //
        // One row per ACCOUNT since 2026-10-18 (several Google, several
        // Outlook). A database that has not had that migration yet lacks the
        // new columns; read the old shape rather than look disconnected.
        const read = (columns: string) => supabase
          .from('calendar_connections')
          .select(columns)
          .eq('user_id', user.id) as unknown as PromiseLike<{ data: ConnectionRow[] | null; error: { code?: string } | null }>
        let { data: connections, error: connError } = await read('id, provider, calendar_id, account_email, is_primary, granted_scopes')
        if (connError?.code === '42703') {
          ({ data: connections, error: connError } = await read('id, provider, calendar_id, granted_scopes'))
        }

        const found = toCalendarAccounts(connections ?? [])

        if (connError || found.length === 0) {
          setIsConnected(false)
          setAccounts([])
          setNeedsReconnect(false)
          setReconnectProviders([])
          setReconnectAccountIds([])
          setIsLoading(false)
          // No rows is a real answer; a failed read (network down) is not.
          if (connError) scheduleRetry()
          return
        }

        setAccounts(found)
        // The default write calendar lives on the home account's row; it may
        // name a calendar of any account, which then receives new events.
        const home = homeAccount(found)
        const homeRow = (connections ?? []).find((c) => c.id === home?.id)
        setDefaultCalendarIdState(homeRow?.calendar_id ?? null)

        // A connection row is enough to start reading (2026-10-18). Pages
        // used to wait for the test call below — a full trip to Google, ~3–5s
        // on prod — before their own events read could even begin. A token
        // that turns out to be broken still reads as "reconnect": the test
        // call below says so, and so does the page's own read (fetchEvents).
        setIsConnected(true)
        setIsLoading(false)

        // The test call is not first paint: the page's own data goes first.
        await afterPageData()
        if (cancelled) return

        // Connection exists - validate by making a test API call
        // Retry once before declaring connection broken (handles transient failures)
        let validated = false
        let lastErrorMsg = ''

        for (let attempt = 0; attempt < 2; attempt++) {
          const { data, error: validateError } = await supabase.functions.invoke('google-calendar-events', {
            body: {
              startDate: new Date().toISOString(),
              endDate: new Date().toISOString(),
            },
          })

          if (!validateError && !data?.error) {
            validated = true
            const broken = (data?.providerErrors ?? []) as Array<{ provider: string; connectionId?: string; needsReconnect: boolean }>
            setReconnectProviders(broken.filter((e) => e.needsReconnect).map((e) => e.provider).filter(isCalendarProvider))
            setReconnectAccountIds(broken.filter((e) => e.needsReconnect && e.connectionId).map((e) => e.connectionId!))
            if (typeof data?.calendarListMissing === 'boolean') setCalendarListMissing(data.calendarListMissing)
            break
          }

          // A non-2xx carries the function's reason in its body, not in the
          // error's message ("Edge Function returned a non-2xx status code").
          const body = validateError ? await functionErrorBody(validateError) : null
          lastErrorMsg = data?.error || body?.error || validateError?.message || ''
          if (body?.needsReconnect === true) lastErrorMsg = `invalid_grant: ${lastErrorMsg}`
          if (attempt === 0) {
            // Wait briefly before retry
            await new Promise(r => setTimeout(r, 1000))
          }
        }

        if (validated) {
          validatedFor = { userId: user.id, at: Date.now() }
          setIsConnected(true)
          setNeedsReconnect(false)
          setError(null)
        } else if (
          lastErrorMsg.includes('invalid_grant') ||
          lastErrorMsg.includes('Token has been expired or revoked') ||
          lastErrorMsg.includes('Unauthorized') ||
          lastErrorMsg.includes('No calendar connection found')
        ) {
          console.warn('Calendar connection invalid after retry, needs reconnect:', lastErrorMsg)
          setIsConnected(false)
          setNeedsReconnect(true)
          setError('Calendar connection expired. Please reconnect.')
        } else {
          // Other errors - connection might still be valid
          console.warn('Calendar validation failed but connection may be valid:', lastErrorMsg)
          setIsConnected(true)
          setNeedsReconnect(false)
        }
      } catch (err) {
        console.error('Error checking calendar connection:', err)
        setIsConnected(false)
        setNeedsReconnect(false)
        scheduleRetry()
      } finally {
        setIsLoading(false)
      }
    }

    void runCheck()

    // A session arriving after mount (the wall coming online, a sign-in) is
    // the moment to look again — with a fresh attempt budget. A restore of
    // the session we just validated is not (see runCheck).
    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        attempts = 0
        void runCheck({ sessionUserId: session?.user?.id })
      }
    })

    return () => {
      cancelled = true
      if (retryTimer) clearTimeout(retryTimer)
      sub.subscription.unsubscribe()
    }
  }, [])

  // Start the OAuth flow for a provider (Google by default). Calendar only
  // unless `mail` is asked for: mail is optional and consented to separately.
  const connect = useCallback(async (provider: CalendarProvider = 'google', options: { mail?: boolean } = {}) => {
    try {
      const { data, error } = await supabase.functions.invoke(authUrlFunctionFor(provider), {
        body: {
          redirectUri: `${window.location.origin}/calendar-callback`,
          ...(provider === 'google' && options.mail ? { access: 'calendar_mail' } : {}),
        },
      })

      if (error) {
        // Try to get the actual error message from the response
        console.error('Edge function error:', error)
        // The error context might have the response body
        if ('context' in error && error.context) {
          try {
            const errorBody = await (error.context as Response).json()
            console.error('Error details:', errorBody)
          } catch {
            // ignore
          }
        }
        throw error
      }

      if (!data?.url) {
        console.error('No URL returned from edge function:', data)
        throw new Error(data?.error || 'No auth URL returned')
      }

      // Redirect to the provider's sign-in
      window.location.href = data.url
    } catch (err) {
      console.error('Failed to connect:', err)
      throw err
    }
  }, [])

  // Disconnect ONE account, or every account of a provider (Google by
  // default). Other accounts keep working. Disconnecting the primary Google
  // account hands that role to the next one (a database trigger).
  const disconnect = useCallback(async (target: CalendarProvider | { connectionId: string } = 'google') => {
    try {
      const { data: { user } } = await getAuthUser()
      if (!user) return

      const gone = typeof target === 'string'
        ? accounts.filter((a) => a.provider === target)
        : accounts.filter((a) => a.id === target.connectionId)
      const goneIds = new Set(gone.map((a) => a.id))

      const query = supabase.from('calendar_connections').delete().eq('user_id', user.id)
      const { error: deleteError } = typeof target === 'string'
        ? await query.eq('provider', target)
        : await query.eq('id', target.connectionId)
      if (deleteError) throw deleteError

      let remaining = accounts.filter((a) => !goneIds.has(a.id))
      // The database picks the next primary (oldest remaining, a trigger); read
      // back what it chose, and that account's own "Create events on".
      if (remaining.length > 0 && gone.some((a) => a.isPrimary)) {
        const { data: rows, error: readError } = await (supabase
          .from('calendar_connections')
          .select('id, provider, calendar_id, account_email, is_primary, granted_scopes')
          .eq('user_id', user.id) as unknown as PromiseLike<{ data: ConnectionRow[] | null; error: unknown }>)
        if (!readError && rows) {
          remaining = toCalendarAccounts(rows)
          const home = homeAccount(remaining)
          setDefaultCalendarIdState(rows.find((r) => r.id === home?.id)?.calendar_id ?? null)
        }
      }
      setAccounts(remaining)
      setReconnectAccountIds((prev) => prev.filter((id) => !goneIds.has(id)))
      setReconnectProviders((prev) => prev.filter((p) => remaining.some((a) => a.provider === p)))
      setIsConnected(remaining.length > 0)
      setNeedsReconnect(false)
      // Events without a provider tag are Google's (cache / older shape).
      setEvents((prev) => (remaining.length === 0 ? [] : prev.filter((e) => {
        if (e.connection_id) return !goneIds.has(e.connection_id)
        return typeof target === 'string' ? (e.provider ?? 'google') !== target : true
      })))
      setError(null)
    } catch (err) {
      console.error('Failed to disconnect:', err)
      throw err
    }
  }, [accounts])

  const makePrimary = useCallback(async (connectionId: string) => {
    const { data, error: rpcError } = await supabase.rpc('make_calendar_connection_primary', { p_connection_id: connectionId })
    if (rpcError || data !== true) {
      console.error('Failed to make account primary:', rpcError)
      throw rpcError ?? new Error('Could not change the primary account')
    }
    const target = accounts.find((a) => a.id === connectionId)
    setAccounts(toCalendarAccounts(accounts.map((a) => ({
      id: a.id,
      provider: a.provider,
      calendar_id: null,
      account_email: a.accountEmail,
      is_primary: a.provider === target?.provider ? a.id === connectionId : a.isPrimary,
      granted_scopes: a.grantsMail ? GMAIL_READ_SCOPE : null,
    }))))
    // The default write calendar is the new home account's own.
    const nextAccounts = accounts.map((a) => ({ ...a, isPrimary: a.provider === target?.provider ? a.id === connectionId : a.isPrimary }))
    if (target && homeAccount(nextAccounts)?.id === connectionId) {
      const { data: row } = await supabase.from('calendar_connections').select('calendar_id').eq('id', connectionId).maybeSingle()
      setDefaultCalendarIdState(row?.calendar_id ?? null)
    }
    emitCalendarChanged()
  }, [accounts])

  // Fetch events for a date range
  const fetchEvents = useCallback(async (startDate: Date, endDate: Date, domainOverride?: string) => {
    if (!isConnected) {
      setEvents([])
      return []
    }

    // Set fetching state and clear error
    setError(null)
    setIsFetching(true)

    try {
      const domain = domainOverride ?? 'universal' // Always fetch all calendars — domain filtering applies to tasks only
      const key = `${getCurrentAccount() ?? 'anon'}|${startDate.toISOString()}|${endDate.toISOString()}|${domain}`
      const recent = recentEventsFor(key)
      if (recent && Date.now() - recent.at < RECENT_EVENTS_MS) {
        if (typeof recent.data.calendarListMissing === 'boolean') setCalendarListMissing(recent.data.calendarListMissing)
        setEvents(recent.data.events || [])
        return recent.data.events || []
      }
      const { data, error: fetchError } = await shareInFlight(`google-calendar-events|${key}`, () => supabase.functions.invoke('google-calendar-events', {
        body: { startDate: startDate.toISOString(), endDate: endDate.toISOString(), domain },
      }))

      // A non-2xx carries the function's reason in its body; without reading
      // it the banner could only say "Edge Function returned a non-2xx status
      // code" (2026-10-16).
      const failure = fetchError ? (await functionErrorBody(fetchError)) ?? { error: fetchError.message } : data

      // Check for auth errors that might indicate token refresh failure
      if (failure?.error) {
        // Common auth error patterns
        if (
          failure.needsReconnect === true ||
          failure.error.includes('Unauthorized') ||
          failure.error.includes('invalid_grant') ||
          failure.error.includes('Token has been expired or revoked') ||
          failure.error.includes('Token')
        ) {
          setError('Calendar connection expired. Please reconnect.')
          setIsConnected(false)
          setNeedsReconnect(true)
          return []
        }
        throw new Error(failure.error)
      }

      // Domain-filtered answers can return early without the flag; keep the last known.
      if (typeof data?.calendarListMissing === 'boolean') setCalendarListMissing(data.calendarListMissing)
      rememberEvents(key, data)
      setEvents(data.events || [])
      return data.events || []
    } catch (err) {
      console.error('Failed to fetch events:', err)
      const message = err instanceof Error ? err.message : 'Failed to fetch events'
      setError(message)
      setEvents([])
      return []
    } finally {
      setIsFetching(false)
    }
  }, [isConnected])

  // Fetch today's events
  const fetchTodayEvents = useCallback(async () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const tomorrow = new Date(today)
    tomorrow.setDate(tomorrow.getDate() + 1)
    return fetchEvents(today, tomorrow)
  }, [fetchEvents])

  // Fetch this week's events
  const fetchWeekEvents = useCallback(async () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const nextWeek = new Date(today)
    nextWeek.setDate(nextWeek.getDate() + 7)
    return fetchEvents(today, nextWeek)
  }, [fetchEvents])

  // Fetch list of all calendars with permissions
  const fetchCalendarList = useCallback(async (): Promise<GoogleCalendarInfo[]> => {
    if (!isConnected) {
      return []
    }

    try {
      const { data, error: fetchError } = await supabase.functions.invoke('google-calendar-list', {
        body: {},
      })

      if (fetchError) throw fetchError

      // Check for auth errors
      if (data?.error) {
        if (
          data.error.includes('Unauthorized') ||
          data.error.includes('invalid_grant') ||
          data.error.includes('Token has been expired or revoked') ||
          data.needsReconnect
        ) {
          setError('Calendar connection expired. Please reconnect.')
          setIsConnected(false)
          setNeedsReconnect(true)
          return []
        }
        throw new Error(data.error)
      }

      return data.calendars || []
    } catch (err) {
      console.error('Failed to fetch calendar list:', err)
      const message = err instanceof Error ? err.message : 'Failed to fetch calendar list'
      setError(message)
      return []
    }
  }, [isConnected])

  // Create a new calendar event
  // Throws CalendarReconnectError if permissions are expired (catch this to show reconnect UI)
  const createEvent = useCallback(async (params: CreateEventParams): Promise<CreateEventResult> => {
    if (!isConnected) {
      throw new Error('Not connected to Google Calendar')
    }

    // Default to browser timezone if not specified
    const timeZone = params.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone

    const { data, error } = await supabase.functions.invoke('google-calendar-create-event', {
      body: {
        title: params.title,
        description: params.description,
        startTime: params.startTime.toISOString(),
        endTime: params.endTime.toISOString(),
        location: params.location,
        allDay: params.allDay,
        timeZone,
        requestId: params.requestId,
        calendarId: params.calendarId,
        recurrence: params.recurrence,
      },
    })

    if (error) throw error

    // Check for auth errors that require reconnection
    if (data?.error) {
      const isAuthError =
        data.error.includes('Unauthorized') ||
        data.error.includes('invalid_grant') ||
        data.error.includes('Token has been expired or revoked') ||
        data.needsReconnect

      if (isAuthError) {
        setError('Calendar connection expired. Please reconnect.')
        setIsConnected(false)
        setNeedsReconnect(true)
        throw new CalendarReconnectError()
      }
      throw new Error(data.error)
    }

    // Anything holding its own copy of the calendar — the planning calendar
    // behind the day tiles — has no realtime channel to learn this from.
    emitCalendarChanged()
    return { id: data.eventId, htmlLink: data.htmlLink }
  }, [isConnected])

  // Update an existing calendar event
  // Throws CalendarReconnectError if permissions are expired (catch this to show reconnect UI)
  const updateEvent = useCallback(async (params: UpdateEventParams): Promise<void> => {
    if (!isConnected) {
      throw new Error('Not connected to Google Calendar')
    }

    const tz = params.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone

    // Log the parameters being sent for debugging
    logger.debug('Updating event:', {
      eventId: params.eventId,
      calendarId: params.calendarId || 'primary',
      location: params.location,
      startTime: params.startTime?.toISOString(),
      endTime: params.endTime?.toISOString(),
    })

    // Use google-calendar-create-event which handles both create and update requests
    // It checks for eventId in the body to determine if it's an update
    const { data, error } = await supabase.functions.invoke('google-calendar-create-event', {
      body: {
        eventId: params.eventId,
        title: params.title,
        location: params.location,
        startTime: params.startTime?.toISOString(),
        endTime: params.endTime?.toISOString(),
        timeZone: tz,
        calendarId: params.calendarId || 'primary', // Default to primary if not specified
      },
    })

    if (error) {
      // Log the full error for debugging
      console.error('Supabase function error:', error)
      throw error
    }

    // Check for errors in the response data
    if (data?.error) {
      const errorMessage = data.error
      console.error('Function returned error:', errorMessage, 'Status:', data.statusCode)
      
      const isAuthError =
        errorMessage.includes('Unauthorized') ||
        errorMessage.includes('invalid_grant') ||
        errorMessage.includes('Token has been expired or revoked') ||
        errorMessage.includes('No calendar connection found') ||
        data.needsReconnect

      if (isAuthError) {
        setError('Calendar connection expired. Please reconnect.')
        setIsConnected(false)
        setNeedsReconnect(true)
        throw new CalendarReconnectError()
      }
      throw new Error(errorMessage)
    }

    // Patch the local cache in place. A destructive fetchTodayEvents() here
    // wiped any non-today window (updating a Thursday event blanked that view)
    // and made same-day edits look like no-ops while the refetch raced. The
    // next natural fetch reconciles with Google's truth.
    setEvents(prev => prev.map(e => {
      const key = e.google_event_id ?? e.id
      if (key !== params.eventId) return e
      const patched = { ...e }
      if (params.startTime) {
        patched.start_time = params.startTime.toISOString()
        patched.startTime = params.startTime.toISOString()
      }
      if (params.endTime) {
        patched.end_time = params.endTime.toISOString()
        patched.endTime = params.endTime.toISOString()
      }
      if (params.location !== undefined) patched.location = params.location ?? undefined
      if (params.title !== undefined) patched.title = params.title
      return patched
    }))
    emitCalendarChanged()
  }, [isConnected])

  // Optimistically remove an event from the local cache.
  // Caller is responsible for orchestrating any undo + the actual API call.
  const removeEventLocal = useCallback((eventId: string) => {
    setEvents(prev => filterOutEvent(prev, eventId))
  }, [])

  // Restore a previously removed event into the local cache.
  const restoreEventLocal = useCallback((event: CalendarEvent) => {
    setEvents(prev => {
      // Match on google_event_id first — cached events from the edge function
      // have no `id` field, and undefined === undefined would false-positive.
      const key = event.google_event_id ?? event.id
      const exists = key != null && prev.some(e => (e.google_event_id ?? e.id) === key)
      if (exists) return prev
      return [...prev, event]
    })
  }, [])

  // Delete an event (or recurring series) via Google Calendar.
  // Throws CalendarReconnectError if permissions are expired.
  const deleteEvent = useCallback(async (params: DeleteEventParams): Promise<void> => {
    if (!isConnected) {
      throw new Error('Not connected to Google Calendar')
    }

    const { data, error } = await supabase.functions.invoke('google-calendar-delete-event', {
      body: {
        eventId: params.eventId,
        calendarId: params.calendarId || 'primary',
        deleteSeries: params.deleteSeries === true,
      },
    })

    if (error) throw error

    if (data?.error) {
      const isAuthError =
        data.error.includes('Unauthorized') ||
        data.error.includes('invalid_grant') ||
        data.error.includes('Token has been expired or revoked') ||
        data.error.includes('No calendar connection found') ||
        data.needsReconnect

      if (isAuthError) {
        setError('Calendar connection expired. Please reconnect.')
        setIsConnected(false)
        setNeedsReconnect(true)
        throw new CalendarReconnectError()
      }
      throw new Error(data.error)
    }

    // Clear any "needs discussion" flag for this event's base id (eager cleanup
    // so the kiosk For-Discussion list doesn't show orphaned items pointing at
    // a Google event that no longer exists).
    try {
      const { data: { user } } = await getAuthUser()
      if (user) {
        const baseId = getRecurringBaseId(params.eventId)
        await supabase
          .from('event_discussion_flags')
          .delete()
          .eq('user_id', user.id)
          .eq('google_event_base_id', baseId)
      }
    } catch (err) {
      console.warn('Failed to clear discussion flag for deleted event:', err)
      // Non-fatal: realtime sub will eventually reconcile, and the orphan is harmless.
    }
    emitCalendarChanged()
  }, [isConnected])

  // Move an event between calendars (Google events.move endpoint)
  const moveEvent = useCallback(async (params: MoveEventParams): Promise<void> => {
    if (!isConnected) {
      throw new Error('Not connected to Google Calendar')
    }

    const { data, error } = await supabase.functions.invoke('google-calendar-move-event', {
      body: {
        eventId: params.eventId,
        sourceCalendarId: params.sourceCalendarId,
        destinationCalendarId: params.destinationCalendarId,
      },
    })

    if (error) throw error

    if (data?.error) {
      const isAuthError =
        data.error.includes('Unauthorized') ||
        data.error.includes('invalid_grant') ||
        data.error.includes('Token has been expired or revoked') ||
        data.needsReconnect

      if (isAuthError) {
        setError('Calendar connection expired. Please reconnect.')
        setIsConnected(false)
        setNeedsReconnect(true)
        throw new CalendarReconnectError()
      }
      throw new Error(data.error)
    }
  }, [isConnected])

  /** Persist the default write calendar (null = the home account's own). It
   *  is stored on the home account's row and may name any account's calendar. */
  const setDefaultCalendarId = useCallback(async (calendarId: string | null) => {
    const previous = defaultCalendarId
    setDefaultCalendarIdState(calendarId)
    const { data: { user } } = await getAuthUser()
    if (!user) return
    const primary = homeAccount(accounts)
    const query = supabase
      .from('calendar_connections')
      .update({ calendar_id: calendarId, updated_at: new Date().toISOString() })
      .eq('user_id', user.id)
    const { error: updateError } = primary
      ? await query.eq('id', primary.id)
      : await query.eq('provider', 'google')
    if (updateError) {
      console.error('Failed to save default calendar:', updateError)
      setDefaultCalendarIdState(previous)
      throw updateError
    }
  }, [defaultCalendarId, accounts])

  const value: GoogleCalendarContextValue = {
    isConnected,
    connectedProviders,
    accounts,
    reconnectAccountIds,
    needsReconnect,
    reconnectProviders,
    calendarListMissing,
    isLoading,
    isFetching,
    events,
    error,
    connect,
    disconnect,
    makePrimary,
    fetchEvents,
    fetchTodayEvents,
    fetchWeekEvents,
    fetchCalendarList,
    createEvent,
    updateEvent,
    moveEvent,
    deleteEvent,
    removeEventLocal,
    restoreEventLocal,
    defaultCalendarId,
    setDefaultCalendarId,
  }

  return (
    <GoogleCalendarContext.Provider value={value}>
      {children}
    </GoogleCalendarContext.Provider>
  )
}

/** How long to wait before re-checking a calendar connection that could not
 *  be checked (network not up yet, session not restored). */
const CONNECTION_CHECK_RETRY_MS = 30_000
/** ~10 minutes of trying — a kiosk booting before its Wi-Fi is well inside that. */
const CONNECTION_CHECK_MAX_ATTEMPTS = 20
/** A good check stands this long against session-restore events. */
const CONNECTION_RECHECK_AFTER_MS = 10 * 60_000

export function useGoogleCalendar() {
  const context = useContext(GoogleCalendarContext)
  if (!context) {
    throw new Error('useGoogleCalendar must be used within a GoogleCalendarProvider')
  }
  return context
}
